import { DOCUMENT } from "@angular/common";
import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Inject,
    Input,
    OnDestroy,
    OnInit,
    Output,
    TemplateRef,
    ViewChild,
} from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { DomSanitizer, SafeUrl } from "@angular/platform-browser";
import { DealerBranding } from "@app/entity/model/dealer-branding";
import { DealerBrandingDbService } from "@app/entity/services/dealer-branding-db.service";
import { DealerBrandingService } from "@app/entity/services/dealer-branding.service";
import { AppHelperService } from "@app/shared/services/app-helper.service";
import { WINDOW } from "@app/shared/tokens/window";
import { contactValid } from "@app/shared/validators/contactValid";
import { phoneRegex } from "@app/shared/validators/phoneValid";
import { BehaviorSubject } from "rxjs/internal/BehaviorSubject";
import { Observable } from "rxjs/internal/Observable";
import { combineLatest } from "rxjs/internal/observable/combineLatest";
import { iif } from "rxjs/internal/observable/iif";
import { of } from "rxjs/internal/observable/of";
import { Subject } from "rxjs/internal/Subject";
import { debounceTime, distinctUntilChanged, map, shareReplay, startWith, switchMap, take, tap } from "rxjs/operators";

interface SvgChange {
    type: string;
    xml: string;
}
const isSubmitSvgType = (svgType: string): boolean => svgType === "submit";
@Component({
    selector: "app-dealer-branding-management",
    templateUrl: "./dealer-branding-management.component.html",
    styleUrls: ["./dealer-branding-management.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DealerBrandingManagementComponent implements OnInit, OnDestroy {
    @Input() entityId: number;
    @Input() accountId: number;
    @Input() pngKey: string;
    @Output() emitImagePNG = new EventEmitter<DealerBranding>();
    customScreenForm: FormGroup = this.formBuilder.group({
        tag1Line: ["Welcome to your new Smart and Secure Home!", Validators.required],
        tag2Line: ["Contact your dealer to activate", Validators.required],
        logo: [""],
        contactLine1: ["", [contactValid, Validators.required]],
        contactLine2: ["", contactValid],
        contactLine3: ["", contactValid],
    });
    formChanges$: Observable<FormGroup>;
    logoChanges: BehaviorSubject<Blob> = new BehaviorSubject(null);
    createdImagePNG: DealerBranding;
    logoName: string;
    readonly imageUrl$ = new Subject<SafeUrl>();
    @ViewChild("previewImage") previewImage: TemplateRef<any>;

    constructor(
        private readonly dealerBrandingService: DealerBrandingService,
        private readonly dealerBrandingDbService: DealerBrandingDbService,
        private readonly formBuilder: FormBuilder,
        private readonly sanitizer: DomSanitizer,
        private readonly dialog: MatDialog,
        @Inject(WINDOW) private readonly _window: Window,
        @Inject(DOCUMENT) private _document: Document
    ) {}

    ngOnInit(): void {
        this.setFormChanges();
        this.setLogoUpload();
    }

    ngOnDestroy(): void {
        const imageInput = this._document.getElementById("logoUpload");
        imageInput.remove();
    }

    get fields() {
        return this.customScreenForm.controls;
    }

    uploadLogo() {
        const imageInput = this._document.getElementById("logoUpload");
        imageInput.click();
    }

    setFormChanges() {
        this.formChanges$ = combineLatest([
            this.customScreenForm.valueChanges.pipe(startWith(this.customScreenForm)),
            this.handleLogoUpload(),
        ]).pipe(
            map(([_, logo]) => {
                if (logo) {
                    this.fields.logo.setValue(logo, { emitEvent: false });
                    this.fields.logo.markAsTouched();
                }
                this.formatPhoneContacts();
                return this.customScreenForm;
            }),
            shareReplay(1)
        );

        this.fields["contactLine3"].valueChanges.pipe(debounceTime(300), distinctUntilChanged()).subscribe((value: string) => {
            this.fields["contactLine3"].setValue(value.toLowerCase());
        });

        this.fields["contactLine2"].valueChanges.pipe(debounceTime(300), distinctUntilChanged()).subscribe((value: string) => {
            this.fields["contactLine2"].setValue(value.toLowerCase());
        });

        this.fields["contactLine1"].valueChanges.pipe(debounceTime(300), distinctUntilChanged()).subscribe((value: string) => {
            this.fields["contactLine1"].setValue(value);
        });
    }

    setLogoUpload() {
        const imageInput = this._document.createElement("input");
        imageInput.id = "logoUpload";
        imageInput.type = "file";
        imageInput.hidden = true;
        imageInput.accept = "image/png, image/jpg, image/jpeg";
        this._document.body.appendChild(imageInput);
        imageInput.addEventListener("change", () => {
            const logoImg = imageInput.files[0];
            this.logoChanges.next(logoImg);
            this.fields.logo.setValue("");
        });
    }

    handleLogoUpload() {
        return this.logoChanges.pipe(
            tap((logoImg: File | null) => {
                this.logoName = logoImg?.name;
            }),
            switchMap((logoImg: Blob) => iif(() => !!logoImg, this.dealerBrandingDbService.convertImage(logoImg), of(logoImg)))
        );
    }

    openPreviewCustomScreen() {
        const dialogRef = this.dialog.open(this.previewImage, { panelClass: ["dialog-no-shadow", "dialog-no-padding"] });
        dialogRef
            .afterClosed()
            .pipe(take(1))
            .subscribe(_ => this.imageUrl$.next(null));
    }

    closePreview(): void {
        this.imageUrl$.next(null);
    }

    onSvgChanges(svg: SvgChange): void {
        const canvas = this._document.createElement("canvas");
        canvas.width = 1024;
        canvas.height = 600;
        const b64SvgEncoding = this._window.btoa(svg.xml);
        const downloadUrl = `data:image/svg+xml;base64,${b64SvgEncoding}`;
        const img = new Image();
        img.crossOrigin = "Anonymous";
        img.onload = () => {
            canvas.getContext("2d").drawImage(img, 0, 0);
            canvas.toBlob(blob => {
                if (isSubmitSvgType(svg.type)) {
                    this.createdImagePNG = {
                        key: this.pngKey,
                        imageFile: blob,
                    };
                    this.createImage();
                    return;
                }
                this.imageUrl$.next(this.createImageUrl(blob));
            });
        };
        img.src = downloadUrl;
    }

    /**
     * Creates a browser-accepted url for user to download
     *
     * @param  {Blob} image File to be converted to url for download
     * @returns {SafeUrl} User downloads file in form of url
     */
    createImageUrl(image: Blob): SafeUrl {
        return this.sanitizer.bypassSecurityTrustUrl(URL.createObjectURL(image));
    }

    createImage() {
        this.emitImagePNG.emit(this.createdImagePNG);
    }

    formatPhoneContacts() {
        const formValue = this.customScreenForm.value;
        for (const control in formValue) {
            if (
                (control?.includes("contactLine1") || control?.includes("contactLine2") || control?.includes("contactLine3")) &&
                phoneRegex.test(formValue[control])
            ) {
                AppHelperService.blurPhoneFieldHandler(this.customScreenForm, control, "(xxx)xxx-xxxx");
            }
        }
    }

    resetLogo() {
        const imageInput = this._document.getElementById("logoUpload");
        imageInput.remove();
        this.logoChanges.next(null);
        this.fields.logo.setValue("");
        this.setLogoUpload();
    }
}
